import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';

/**
 * Revue d'un essai, question par question (Feed → /attempts/:id). Terminé pour
 * tous ; en cours aussi pour un admin (migration 0504).
 * RPC `get_attempt_review` (migration 0272, security definer) : lecture pour
 * l'élève, un prof lié, un parent lié ou un admin.
 */

const stepSchema = z.object({
  prompt_md: z.string().nullable().catch(null),
  chosen_mds: z.array(z.string().nullable()).catch([]),
  correct_mds: z.array(z.string().nullable()).catch([]),
  answer_value: z.number().nullable().catch(null),
  was_correct: z.boolean().nullable().catch(null),
  time_ms: z.number().nullable().catch(null),
  hint_used: z.boolean().nullable().catch(null),
});

export const attemptReviewSchema = z.object({
  attempt_id: z.string(),
  exercise_id: z.string().nullable(),
  exercise_title: z.string().nullable(),
  subject: z.string().nullable(),
  content_version: z.string().nullable(),
  is_parametric: z.boolean().catch(false),
  student_id: z.string(),
  student_name: z.string().nullable(),
  student_avatar: z.string().nullable(),
  score: z.number().nullable(),
  completed_at: z.string().nullable(),
  // Migration 0504 : un admin peut ouvrir un essai EN COURS.
  status: z.string().nullable().catch(null).default(null),
  started_at: z.string().nullable().catch(null).default(null),
  question_count: z.number().nullable().catch(null).default(null),
  steps: z.array(stepSchema).catch([]),
});

export type AttemptReview = z.infer<typeof attemptReviewSchema>;
export type AttemptReviewStep = z.infer<typeof stepSchema>;

/** Traduit les erreurs de la RPC en message pour l'utilisateur. */
export function translateAttemptReviewError(message: string | null | undefined): string {
  const m = message ?? '';
  if (m.includes('not_authorized')) return 'Tu n’as pas accès aux résultats de cet essai.';
  if (m.includes('attempt_not_found')) return 'Cet essai est introuvable.';
  if (m.includes('attempt_not_completed')) return 'Cet essai n’est pas encore terminé.';
  if (m.includes('not_authenticated')) return 'Connecte-toi pour voir ces résultats.';
  return 'Impossible de charger les résultats. Réessaie dans un instant.';
}

/** Retire les réponses vides renvoyées par la RPC. */
export function cleanAnswers(mds: readonly (string | null)[]): string[] {
  return mds.filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
}

export function useAttemptReview(attemptId: string | null | undefined) {
  return useQuery({
    queryKey: ['attempt-review', attemptId],
    enabled: !!attemptId,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<AttemptReview> => {
      // RPC postérieure aux types générés → client générique.
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db.rpc('get_attempt_review', { p_attempt_id: attemptId });
      if (error) throw new Error(translateAttemptReviewError(error.message));
      // Réponse vide / inattendue : jamais le détail technique (zod) à l'écran.
      const parsed = attemptReviewSchema.safeParse(data);
      if (!parsed.success) throw new Error(translateAttemptReviewError('attempt_not_found'));
      return parsed.data;
    },
  });
}
